import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, PlusCircle, FileText, BarChart3,
  User, Settings, LogOut, Menu, X, Stethoscope,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import OfflineIndicator from "@/components/OfflineIndicator";
import InstallPrompt from "@/components/InstallPrompt";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/cases/new", label: "Log Case", icon: PlusCircle, id: "nav-new-case" },
  { to: "/cases", label: "Case Log", icon: FileText, id: "nav-cases" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, id: "nav-analytics" },
  { to: "/profile", label: "Profile", icon: User, id: "nav-profile" },
  { to: "/settings", label: "Settings", icon: Settings, id: "nav-settings" },
];

function NavItem({ to, label, icon: Icon, id, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={id}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-5 py-3 mx-3 rounded-xl transition-all ${
          isActive
            ? "bg-neu-bg/60 border-l-4 border-neu-accent text-neu-accent font-semibold"
            : "text-neu-text hover:text-neu-accent border-l-4 border-transparent"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={`w-5 h-5 transition-colors ${
              isActive ? "text-neu-accent" : "text-neu-muted group-hover:text-neu-accent"
            }`}
          />
          <span className="font-medium">{label}</span>
        </>
      )}
    </NavLink>
  );
}

function SidebarContent({ user, onLogout, onClose }) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-8 pb-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-neu-accent flex items-center justify-center shadow-lg shadow-neu-accent/30">
          <Stethoscope className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="heading text-xl leading-none">FellowLog</div>
          <div className="text-xs subtle mt-1">Surgical case log</div>
        </div>
      </div>
      <nav className="flex-1 flex flex-col gap-1 mt-2">
        {NAV.map((n) => (
          <NavItem key={n.to} {...n} onClick={onClose} />
        ))}
      </nav>
      <div className="p-4 border-t border-neu-muted/20 mx-3 mb-4">
        {user && (
          <div className="px-2 py-3">
            <div className="text-xs subtle uppercase tracking-wider">Signed in as</div>
            <div className="font-semibold text-neu-text truncate" data-testid="sidebar-user-name">
              {user.name}
            </div>
            <div className="text-xs subtle truncate">{user.email}</div>
          </div>
        )}
        <button
          onClick={onLogout}
          data-testid="nav-logout"
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-neu-text hover:text-red-600 transition-colors"
        >
          <LogOut className="w-5 h-5 text-neu-muted" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/signin");
  };

  return (
    <div className="min-h-screen bg-neu-bg flex">
      <OfflineIndicator />
      <InstallPrompt />
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col w-64 min-h-screen bg-neu-surface shadow-neu"
        data-testid="sidebar-desktop"
      >
        <SidebarContent user={user} onLogout={handleLogout} onClose={() => {}} />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-neu-surface shadow-neu-sm flex items-center justify-between px-4 h-14">
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-toggle"
          className="p-2 rounded-lg text-neu-text"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <Stethoscope className="w-5 h-5 text-neu-accent" />
          <span className="heading text-lg">FellowLog</span>
        </div>
        <div className="w-10" />
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div
            className="fixed inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="relative w-72 bg-neu-surface shadow-neu animate-slide-in">
            <button
              className="absolute top-4 right-4 p-2 rounded-lg text-neu-text"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-drawer-close"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent user={user} onLogout={handleLogout} onClose={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <main className="flex-1 lg:ml-0 pt-14 lg:pt-0 min-w-0">
        <div className="p-6 md:p-8 max-w-7xl mx-auto">{children}</div>
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
